import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { useAppNavigation } from 'lib/routing/useAppNavigation'
import { useCallback, useContext, useEffect } from 'react'
import * as React from 'react'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import type { ReactNode } from 'react'
import type { AppRouteMatch } from './routeTypes'

import { selectLoggedIn } from 'selectors/accountSelectors'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { pushWithRegion } from 'actions/NavigationActions'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import { isCheckout } from 'selectors/checkoutSelectors'
import { getCheckoutDepth } from 'checkout/hooks/useCheckoutRouteTracker'

interface Props {
  children?: ReactNode
  match: AppRouteMatch | null
  isLoggedIn: boolean
  regionCode: string
  showPopup?: boolean
  loader?: React.ReactNode
  accountAccessModalInitialMode?: App.UiAccountModalMode
}

function AuthRouteComponent(props: Props) {
  const {
    isLoggedIn,
    regionCode,
    showPopup,
    children,
    loader,
    accountAccessModalInitialMode,
  } = props
  const { match } = props

  const dispatch = useAppDispatch()
  const path = match?.path
  const isCheckoutPage = useAppSelector(isCheckout)
  const navigation = useAppNavigation()
  const showModal = useContext(ModalContext)

  /**
   * CheckoutDepth is 0 if we're on the first checkout step.
   * Thus, we sum 1 to get the previous page, which is the offer page.
   */
  const goBackToOfferPage = useCallback(() => {
    const checkoutDepth = getCheckoutDepth()
    navigation.go(-(checkoutDepth + 1))
  }, [navigation])

  useEffect(() => {
    if (showPopup && !isLoggedIn) {
      const useTripPlannerVersion = !!path?.startsWith('/:regionCode/trip-planner/trip/')
      showModal<AccountAccessModalResult>(<AccountAccessModal
        initialMode={accountAccessModalInitialMode || (useTripPlannerVersion ? 'tripPlannerLogin' : 'login')}
      />).then(result => {
        if (result?.loggedIn) {
          // success, let them through
        } else {
          // no success, they closed it or something else
          const isRestoreCart = path?.includes('/checkout-restore-cart')
          if (isCheckoutPage && !isRestoreCart) {
            goBackToOfferPage()
          } else {
            dispatch(pushWithRegion('/'))
          }
        }
      })
    }

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, showPopup, path])

  if (!isLoggedIn && showPopup) {
    // We're waiting for the response on the auth modal now
    if (loader) {
      return loader
    }
    return <LoadingIndicator />
  }

  if (!isLoggedIn) {
    // Don't have access to this route and we don't want to show a login pop up
    // send them directly back to the home page
    return <RedirectWithStatus code={302} to={`/${regionCode.toLowerCase()}`} />
  }

  return <>{children}</>
}

function mapStateToProps(state: App.State) {
  return {
    isLoggedIn: selectLoggedIn(state),
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(AuthRouteComponent)
